import { useTurnsIntegration } from '../../queries';
import { SetupDataStore } from '../../stores/setupDataStore';
import * as providerConfigs from './configs';

export const useProviderConfig = (overrideProvider?: string) => {
    const { setupData } = SetupDataStore.useStore();
    const provider = overrideProvider ?? setupData?.provider;
    const turnsIntegration = useTurnsIntegration();

    let providerKey;

    switch (provider?.toLowerCase()) {
        case 'servicefinance':
            // ServiceFinance always uses the embedded flow.
            // TURNS features (income type, disclosure) are controlled dynamically via turnsIntegration.
            providerKey = 'serviceFinance';
            break;
        case 'goodleap':
            // Two-tier config selection for GoodLeap:
            // 1. goodLeapEmbedded: Flag ON - embedded flow with enhanced UX
            //    - Turns features (industry field, disclosure) controlled dynamically via turnsIntegration
            // 2. goodLeap: Standard GoodLeap (flag OFF)
            //    - Standard flow without embedded features
            providerKey = turnsIntegration.providerFlagEnabled ? 'goodLeapEmbedded' : 'goodLeap';
            break;
        case 'greensky':
            // GreenSky config selection logic:
            // 1. greenskyPrequalification: Prequal flow — used when actively applying for prequalification
            //    OR when continuing a prequalification from the banner (prequalificationId present but
            //    NOT transitioned to hard app). This ensures hasMailingAddress=true aligns with
            //    PrequalificationWorkflow which always renders a mailing address step; without this,
            //    address resolution reads from jobDetails (stale) instead of mailingAddress (user-updated),
            //    causing wrong address sent to backend and missing co-applicant marriage options.
            //    IMPORTANT: When hasTransitionedToHardApp is true, the user entered their address via
            //    Job Details (not mailing address), so we must NOT use greenskyPrequalification — that
            //    would read from the mailingAddress path which was never updated.
            // 2. greenSkyEmbedded/greenSkyEmbeddedTechnician: Embedded flow with PIN challenge
            //    - Uses embedded flow when "greenskyTurns" flag is ON (regardless of BU/tenant settings)
            //    - Turns waterfall (on decline) only happens when fully enabled (requires BU + tenant settings)
            // 3. greenSky: Legacy external application flow
            providerKey =
                setupData?.isApplyingForPrequalification ||
                (setupData?.prequalificationId && !setupData?.hasTransitionedToHardApp)
                    ? 'greenskyPrequalification'
                    : turnsIntegration.providerFlagEnabled
                      ? setupData?.role === 'technician'
                          ? 'greenSkyEmbeddedTechnician'
                          : 'greenSkyEmbedded'
                      : 'greenSky';
            break;
        case 'synchrony':
            providerKey =
                setupData?.role === 'technician'
                    ? 'synchronyTechnician'
                    : setupData?.prequalificationId
                      ? 'synchronyPrequalification'
                      : 'synchrony';
            break;
        case 'wellsfargo':
            // Uses isEnabled (not providerFlagEnabled) because WF has no separate LD flag —
            // providerFlagEnabled is unconditionally true. The embedded prequal form should only
            // appear when Turns is fully enabled (BU + tenant + not degraded).
            providerKey = turnsIntegration.isEnabled ? 'wellsFargoPrequalWithTurns' : 'wellsFargo';
            break;
        case 'turns':
            providerKey = 'turns' as const;
            break;
        case 'financeit':
            providerKey = 'financeit' as const;
            break;
        case 'buyfin':
            providerKey =
                setupData?.role === 'technician' ? 'buyFinTechnician' : ('buyFin' as const);
            break;
        case 'affirm':
            providerKey = 'affirm' as const;
            break;
    }

    if (!providerKey) {
        throw new Error(`Provider not found: ${provider ?? 'undefined or missing'}`);
    }

    return providerConfigs[providerKey as keyof typeof providerConfigs];
};
